// RecordPage (F-185, docs/PAGE-GRAMMAR.md): the one frame of every record screen. Top to bottom: the record's way
// around — Back to the section it was opened from (F-178), the breadcrumb (section › record id), Previous / Next in that
// section's list — then the header (title, status pills, facts, one primary action on the right, the record's other
// actions beside it), the record's banners, its section tabs, and its body (a CardGrid). Controlled and data-free: the
// host resolves the section, the neighbours and the actions; a neighbour the host does not know is drawn plain (never a
// dead link that goes nowhere).

import type { ReactNode } from "react";
import { TextLink } from "../core/Link";
import { PageHeader } from "../g4a/PageHeader";
import { cx } from "../lib/cx";
import type { Hs } from "../lib/hs";
import { G9_LABELS, G9_TEMPLATES } from "./labels";

export interface RecordSection {
  /** The section's name (a navigation label: *Requisitions*, *Interviews*) or the list's own title. */
  label: string;
  /** The list as the person left it (its filters kept), or the section's own route. */
  href: string;
}

export interface RecordNeighbours {
  /** The previous record in the list, or null at its head (drawn plain). */
  previous?: string | null | undefined;
  next?: string | null | undefined;
}

export interface RecordPageProps {
  section: RecordSection;
  /** The record's id as the breadcrumb's last crumb (R-1048, the candidate). */
  record: string;
  /** false where the shell's top bar already draws this breadcrumb (section › record), so it is not drawn twice. */
  crumb?: boolean | undefined;
  title: string;
  /** The status pill(s) after the title. */
  pills?: ReactNode | undefined;
  /** Header facts, separated by middle dots. */
  meta?: readonly ReactNode[] | undefined;
  /** The one primary action, on the right. */
  action?: ReactNode | undefined;
  /** The record's other actions (a menu, a secondary button), before the primary one. */
  more?: ReactNode | undefined;
  /** Omit when the record is not one of a list (no Previous / Next are drawn). */
  neighbours?: RecordNeighbours | undefined;
  /** Banners about the whole record (a hold, a pending change), under the header. */
  banners?: ReactNode | undefined;
  /** The section tabs (SectionTabs). */
  tabs?: ReactNode | undefined;
  loading?: boolean | undefined;
  children?: ReactNode;
  /** Hotspot keys of the three links. */
  hs?: { back?: Hs | undefined; previous?: Hs | undefined; next?: Hs | undefined } | undefined;
  className?: string | undefined;
}

export function RecordPage({
  section, record, crumb = true, title, pills, meta, action, more, neighbours, banners, tabs, loading, children, hs, className,
}: RecordPageProps) {
  const back = G9_TEMPLATES.backTo({ section: section.label });
  const actions = more || action ? <>{more ?? null}{action ?? null}</> : undefined;
  return (
    <div className={cx("mc-rp", className)}>
      <nav className="mc-rp-nav" aria-label={back}>
        <TextLink icon="back" href={section.href} hs={hs?.back} className="mc-rp-back">{back}</TextLink>
        {crumb ? (
          <ol className="mc-rp-crumb">
            <li><a href={section.href}>{section.label}</a></li>
            <li aria-current="page">{record}</li>
          </ol>
        ) : null}
        {neighbours ? (
          <span className="mc-rp-step">
            {neighbours.previous
              ? <TextLink icon="back" href={neighbours.previous} hs={hs?.previous}>{G9_LABELS.previous}</TextLink>
              : <TextLink plain icon="back">{G9_LABELS.previous}</TextLink>}
            {neighbours.next
              ? <TextLink after="fwd" href={neighbours.next} hs={hs?.next}>{G9_LABELS.next}</TextLink>
              : <TextLink plain after="fwd">{G9_LABELS.next}</TextLink>}
          </span>
        ) : null}
      </nav>
      <PageHeader title={title} pills={pills} meta={meta} actions={actions} loading={loading} />
      {banners ?? null}
      {tabs ? <div className="mc-rp-tabs">{tabs}</div> : null}
      {children ?? null}
    </div>
  );
}
